import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import classNames from "classnames";
import { each, isEqual, merge, sortBy } from "lodash";
// @ts-ignore FIXME: strict migration - Could not find a declaration file for module 'react-grid-layout' — third-party package ships no types
import type { Layout } from "react-grid-layout";
// @ts-ignore FIXME: strict migration - Could not find a declaration file for module 'react-grid-layout' — third-party package ships no types
import { Responsive, WidthProvider } from "react-grid-layout";
import { useDispatch, useSelector } from "react-redux";

import { CircularLoader } from "@similarweb/ui-components";

import { updateUniqueSegmentsIds } from "app/actions/segmentsModuleActions";
import { showSuccessToast } from "app/actions/toast_actions";
import { swSettings } from "app/common/services/swSettings";
import type { IChosenItemParams } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import { DashboardWebsiteSidebar } from "app/components/dashboard/dashboardWebsiteSidebar";
import type { IDashboardExtended } from "app/components/dashboard/services/IDashboard";
import DashboardService from "app/components/dashboard/services/DashboardService";
import {
    dashboardWizardIsEditing,
    dashboardWizardNewWidget,
    dashboardWizardSetWidgetId,
} from "app/components/dashboard/widget-wizard/actions/dashboardWizardActionCreators";
import DashboardWizardCountry from "app/components/dashboard/widget-wizard/components/DashboardWizardCountry";
import { StatefulDropdown } from "app/components/dashboard/widget-wizard/components/StatefulDropdown";
import { DashboardWizardV2 } from "app/components/dashboard/widget-wizard/DashboardWizard-v2";
import { DashboardLoader } from "app/components/React/DashboardLoader";
import type { IWidget, IWidgetKeyBase, IWidgetWebsiteKey } from "app/components/widget/widget-types/Widget";
import { emptyWidget } from "app/components/widget/widget-types/Widget";
import {
    getWidgetSubtitleForModal,
    getWidgetTitle,
} from "app/components/widget/widget-utilities/widgetPpt/PptWidgetUtils";
import { AddonPopup } from "app/pages/activation/components/AddonPopup/AddonPopup";
import { DashboardHeaderV2 } from "app/pages/dashboard/dashboard-header/DashboardHeader-v2";
import { getHeaderBannersHeight } from "app/pages/dashboard/dashboard-header/DashboardHeaderStyles";
import DashboardCopyToUserDialog from "app/pages/dashboard/DashboardCopyToUserDialog";
import { setSharedWithMeDashboards } from "app/pages/dashboard/DashboardSideNavActions";
import DashboardSubscriptionService from "app/pages/dashboard/DashboardSubscriptionService";
import { resetDashboard, setDashboardInitialized } from "app/pages/dashboard/redux/actions/CurrentDashboardActions";
import { widgetsLoadedSelector } from "app/pages/dashboard/redux/DashboardSelectors";
import { SharedDashboardModalContainer } from "app/pages/dashboard/SharedDashboardModalContainer";
import { updateLastViewedAsset } from "app/pages/digital-suite/home-page/components/CustomAssets/customAssetsApi";
import { AssetType } from "app/pages/digital-suite/home-page/components/CustomAssets/types";
import { keywordGroupsQueryClient } from "app/services/keyword-groups";
import { PdfExportService } from "app/services/PdfExportService";
import { PptExportService } from "app/services/PptExportService/PptExportService";
import type { IPptExportRequest } from "app/services/PptExportService/PptExportServiceTypes";
import { RecentService } from "app/services/recent/recentService";
import { fetchWebsitesFaviconsQuery } from "app/services/sitesService/queries/sitesQueries";
import { SwTrack } from "app/services/SwTrack";
import { isTrialOrAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/trialUsersUtils";
import type { WebsitePageSearchType } from "pro-features/components/AutocompleteWebsites/types";
import {
    getDomainWithSubdomain,
    getUrlComponents,
} from "pro-features/components/AutocompleteWebsites/urlComponentUtils";
import type { IDashboardExportListItem } from "pro-features/components/Dashboard/DashboardPptExportModal/DashboardPptExportModalTypes";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import SegmentsLimitBanner from "./components/SegmentsLimitBanner/SegmentsLimitBanner";
import { circularLoaderoptions, SEGMENTS_LIMIT_STATUS_MAP } from "./constants";
import { CustomGridItemComponent, ResizeHandleCmp } from "./DashboardPagesStyles";
import { DashboardPptExportModalContainer } from "./DashboardPptExportModalContainer";
import { DashboardLoadingWrapper, GridContainer } from "./DashboardStyles-v2";
import { EmprtyDashboard } from "./EmprtyDashboardComponent";
import * as GridUtilitiesV2 from "./GridUtilities-v2";
import { useSharedUsersList } from "./hooks/useSharedUsersList";
import { selectIsDashboardLoading } from "./redux/reducers/CurrentDashboardReducer";
import { selectLimitationStatus } from "./redux/selectors";
import { allTrackers } from "app/services/track/track";

const ReactGridLayout = WidthProvider(Responsive);

const userDurations = [
    { id: "-1", text: "Select" },
    { id: "28d", text: "28d" },
    { id: "1m", text: "1m" },
    { id: "3m", text: "3m" },
    { id: "6m", text: "6m" },
    { id: "12m", text: "12m" },
    { id: "25m", text: "25m" },
    { id: "37m", text: "37m" },
];

const userWebsources = [
    { id: "-1", text: "Select" },
    { id: "Total", text: "Total" },
    { id: "Desktop", text: "Desktop" },
    { id: "MobileWeb", text: "MobileWeb" },
];

export const DashboardPageV2 = ({ props }: FixmeType) => {
    const uiRouter = props?.transition?.router;
    const t = useTranslation();
    const { dashboardId } = props?.transition?.params();
    const dispatch = useDispatch();
    const isLoading = useSelector(selectIsDashboardLoading);
    const isTrial = isTrialOrAfterTrial();

    const gridContainerRef = useRef<HTMLDivElement>(null);
    const [exportLoaderTitle, setExportLoaderTitle] = useState("");
    const [exportLoaderSubtitle, setExportLoaderSubtitle] = useState("");
    const [isExportLoading, setIsExportLoading] = useState(false);
    const [isPptExportModalOpen, setIsPptExportModalOpen] = useState(false);
    const [widgetsForPptModal, setWidgetsForPptModal] = useState<IDashboardExportListItem[]>([]);
    const [shareDashboardOpen, setShareDashboardOpen] = useState(false);

    const refs = useRef<{ [key: string]: any }>({});
    const [isSubscriptionOn, setIsSubscriptionOn] = useState(false);
    const [isNotificationAllowed, setIsNotificationAllowed] = useState(false);
    const [loading, setIsLoading] = useState(true);
    const [breakpoint, setBreakpoint] = useState();
    const [numOfCols, setNumOfCols] = useState(4);

    const [widgetsKey, setWidgetsKey] = useState(Math.random());
    const navListLoadedSelector = useSelector((state) => state.customDashboard.dashboardSideNav.navListLoaded, isEqual);

    const segmentsLimitationStatus = useSelector(selectLimitationStatus);

    //CurrentDashboard Selectors
    const dashboardWidgetsChangedSelector = useSelector(
        (state) => state.customDashboard.currentDashboard.widgets,
        isEqual,
    );
    const lastDashboardWidgetsChangedSelector = useSelector(
        (state) => state.customDashboard.currentDashboard.lastWidgetUpdated,
        isEqual,
    );

    const widgetsLoaded = useSelector(widgetsLoadedSelector);

    const [currentDashboard, setCurrentDashboard] = useState<IDashboardExtended | null>(null);
    const [showGlobalFilters, setShowGlobalFilters] = useState(false);
    const [isWebsitesSidebarOpen, setIsWebsitesSidebarOpen] = useState(false);
    const [isWizardOpen, setIsWizardOpen] = useState(false);
    const [selectedWidget, setSelectedWidget] = useState(null);
    const [newWidget, setNewWidget] = useState<FixmeType>(null);
    const [userCountries, setUserCountries] = useState<FixmeType[]>([]);
    const [isStatic, setIsStatic] = useState<boolean | undefined>();
    const [isSharingDashboard, setIsSharingDashboard] = useState<boolean>(false);
    const pageHeight = useMemo<number>(() => getHeaderBannersHeight(isTrial), [isTrial]);

    // @ts-ignore FIXME: strict migration - Argument of type 'IDashboardExtended | null' is not assignable to parameter of type 'IDashboardExtended' — hook guards null internally
    useSharedUsersList(currentDashboard);

    const pptService = useRef<PptExportService | null>(null);

    useEffect(() => {
        pptService.current = new PptExportService();

        return () => {
            dispatch(resetDashboard());
            dispatch(updateUniqueSegmentsIds({}));
        };
    }, []);

    useEffect(() => {
        const sharedWithMe = currentDashboard?.isSharedWithMe === undefined ? false : currentDashboard?.isSharedWithMe;
        const breakpointStatic = numOfCols !== 4;
        setIsStatic(sharedWithMe || breakpointStatic);
    }, [breakpoint, numOfCols]);

    useEffect(() => {
        async function initDashboard(dashboardId: string) {
            if (!currentDashboard) {
                const dashboard = await DashboardService.initDashboard(dashboardId);

                dispatch(setDashboardInitialized(dashboard.id, dashboard.widgets, dashboard.title));
                setLayoutFirstTime(true);
                setCurrentDashboard(dashboard);
                updateLastViewedAsset(dashboardId, AssetType.DASHBOARD);
            }
            setIsLoading(false);
        }

        if (navListLoadedSelector === true) {
            const dashboardIdFound = DashboardService.getDashboardById(dashboardId);
            if (!dashboardIdFound) {
                if (DashboardService.dashboards.length > 0) {
                    uiRouter.stateService.go("dashboard-exist", {
                        dashboardId: DashboardService.getFirstDashboard().id,
                    });
                } else {
                    uiRouter.stateService.go("dashboard-new");
                }
            } else {
                initDashboard(dashboardId);
            }
        }
        return () => {
            refs.current = {};
        };
    }, [dashboardId, dispatch, navListLoadedSelector, uiRouter.stateService]);

    const [widgets, setWidgets] = useState<IWidget<any>[]>([]);
    const [layout, setLayout] = useState<FixmeType[]>([]);
    const [layoutFirstTime, setLayoutFirstTime] = useState<boolean>(true);

    async function handleEnlarge(widget: FixmeType) {
        const shouldEnlarge = !widget.pos.sizeY || widget.pos.sizeY === 1;

        const newLayouts = GridUtilitiesV2.enlarge(widget.id, layout, shouldEnlarge);
        // if (!arraysEqual1(newLayouts, layout)) {

        setLayout(newLayouts);
        newLayouts.forEach((item: any) => {
            const widget: FixmeType = currentDashboard?.widgets.find((widget: FixmeType) => item.i === widget.id);

            if (!item.w || item.w < item.minW) {
                item.w = item.minW || 1;
            }

            if (widget) {
                widget.pos.col = item.x;
                widget.pos.row = item.y;
                widget.pos.sizeX = item.w || 1;
                widget.pos.sizeY = item.h || 1;
            }
        });

        if (DashboardService.dashboard?.id && currentDashboard?.id && !currentDashboard?.isSharedWithMe) {
            console.log("handleEnlarge - updateDashboardWidgets");
            await DashboardService.updateDashboardWidgets(currentDashboard);
        }
        Object.keys(refs.current).map((key) => {
            // console.log("current ref= ", refs.current[key]);
            const widget: FixmeType = currentDashboard?.widgets.find((widget: FixmeType) => key === widget.id);
            if (widget?.pos?.sizeY > 1) {
                const { width, height } = refs.current?.[key].chart.container.parentNode.getBoundingClientRect();
                refs.current?.[key].chart.setSize(width, height - 38, false);
            }
        });
    }

    useEffect(() => {
        if (!currentDashboard) {
            return;
        }

        document.title = currentDashboard?.title;

        if (!DashboardService.newVersion) {
            legacyMigrate();
        }

        setShowGlobalFilters(swSettings.components.Home.resources.HasDashboardSettings);
        setIsWebsitesSidebarOpen(false);
        setIsSubscriptionOn(DashboardSubscriptionService.isSubscriptionOn(currentDashboard));
        setIsNotificationAllowed(DashboardSubscriptionService.isNotificationAllowed(currentDashboard));

        setNewWidget(
            merge({}, emptyWidget, {
                dashboardId: currentDashboard?.id,
            }),
        );
        //on dashboard creation : Open the wizard
        if (isJustCreatedDashboard()) {
            openWizard(newWidget);
        }

        setUserCountries(getUserCountries("Home"));
    }, [currentDashboard]);

    //when widgets changed fix the layout and if needed save
    useEffect(() => {
        const tmpWidgets =
            (currentDashboard?.widgets?.length ?? 0) > 0
                ? sortBy(currentDashboard?.widgets, function (widget: any) {
                      return widget._widgetConfig.addedTime;
                  })
                : [];

        //SIM-23335 - gridster doesnt change the "pos" property of a widget if they are not valid, like in this case sizeX or sizeY as a string.
        tmpWidgets.forEach((widget: FixmeType) => {
            ["sizeX", "sizeY"].map((property) => {
                if (widget.pos.hasOwnProperty(property)) {
                    widget.pos[property] = parseInt(widget.pos[property]);
                }
            });
        });

        setWidgets(tmpWidgets);
        const newTestLayout = GridUtilitiesV2.translateWidgetsToLayout(tmpWidgets, !!isStatic);
        handleLayoutChange(newTestLayout);
    }, [currentDashboard, dashboardWidgetsChangedSelector, lastDashboardWidgetsChangedSelector.counter]);

    const navigateToDashboard = useCallback(
        (dashboardId: string) => {
            uiRouter.stateService.go("dashboard-exist", {
                dashboardId,
            });
        },
        [uiRouter],
    );

    //////////START return code. NO MORE HOOKS AFTER THIS!!! ///////

    if (isLoading) {
        return (
            <DashboardLoadingWrapper>
                <CircularLoader options={circularLoaderoptions} />
            </DashboardLoadingWrapper>
        );
    }

    if (!currentDashboard) {
        return null;
    }

    function legacyMigrate() {
        const widgets = currentDashboard?.widgets;

        each(widgets, (widget: FixmeType, index: FixmeType) => {
            merge(widget.pos, getWidgetPos(widget, index));
        });

        function getWidgetSize(widget: FixmeType) {
            return widget?.pos?.sizeX ?? 2;
        }

        function getWidgetCol(widget: FixmeType, index: number) {
            if (index % 2 == 0) {
                return 0;
            } else {
                return 2;
            }
        }

        function getWidgetRow(widget: FixmeType, index: number) {
            if (index == 0 || index == 1) {
                return 0;
            } else {
                if (index % 2 == 0) {
                    return index / 2;
                } else {
                    return (index - 1) / 2;
                }
            }
        }

        function getWidgetPos(widget: FixmeType, index: number) {
            return {
                sizeY: 1,
                sizeX: getWidgetSize(widget),
                col: getWidgetCol(widget, index),
                row: getWidgetRow(widget, index),
            };
        }
    }

    function getUserCountries(component: string) {
        const _component = swSettings.components[component];
        const allowedCountries: FixmeType[] = [];
        _component.allowedCountries.forEach((country: any) => {
            if (!country.children || country.children.length < 1) {
                allowedCountries.push({
                    ...country,
                });
            } else {
                allowedCountries.push({
                    ...country,
                });
                country.children.forEach((state: FixmeType) => {
                    allowedCountries.push({
                        ...state,
                    });
                });
            }
        });
        return [{ id: "0", text: "Select" }, ...allowedCountries];
    }

    function isJustCreatedDashboard() {
        const newlyCreated = proNavigator.current().name === "dashboard.created";
        return currentDashboard?.widgets.length == 0 && newlyCreated;
    }

    const toggleWebsitesSidenav = () => {
        setIsWebsitesSidebarOpen(!isWebsitesSidebarOpen);
    };

    const globalChangeCountry = (countryId: FixmeType) => {
        updateDashboardWidgetsProps({
            country: countryId,
        });
    };
    const globalChangeDuration = (durationId: FixmeType) => {
        updateDashboardWidgetsProps({
            duration: durationId.id,
        });
    };
    const globalChangeWebsource = (websourceId: FixmeType) => {
        updateDashboardWidgetsProps({
            webSource: websourceId.id,
        });
    };

    function updateDashboardWidgetsProps(newProps: FixmeType) {
        currentDashboard?.widgets.forEach((widget: FixmeType) => {
            DashboardService.editWidget({
                properties: Object.assign({}, widget.getProperties(), newProps),
                pos: widget.pos,
                id: widget.id,
                dashboardId: widget.dashboardId,
            });
        });
    }

    const handleResized = (newLayout: FixmeType) => {
        handleLayoutChange(newLayout);
    };

    const handleDragged = (newLayout: FixmeType) => {
        handleLayoutChange(newLayout);
    };

    function arraysEqual(a1: FixmeType, a2: FixmeType) {
        return (
            a1.length === a2.length &&
            a1.every((o: FixmeType, idx: number) => {
                return o.x === a2[idx].x && o.y === a2[idx].y && o.w === a2[idx].w && o.h === a2[idx].h;
            })
        );
    }

    async function handleLayoutChange(newLayout: Readonly<Layout>) {
        if (newLayout.length === 0 && layout?.length === 0) {
            return;
        }

        const noEmptyRowsLayout = clearEmptyRows(newLayout);
        // if (arraysEqual(noEmptyRowsLayout, layout)) {
        //     return;
        // }

        noEmptyRowsLayout.forEach((item: any) => {
            const widget: FixmeType = currentDashboard?.widgets.find((widget: FixmeType) => item.i === widget.id);

            if (!item.w || item.w < item.minW) {
                item.w = item.minW || 1;
            }

            if (widget) {
                widget.pos.col = item.x;
                widget.pos.row = item.y;
                widget.pos.sizeX = item.w || 1;
                widget.pos.sizeY = item.h || 1;
            } else {
            }
        });
        if (!arraysEqual(noEmptyRowsLayout, layout)) {
            setLayout(noEmptyRowsLayout);
            if (
                DashboardService.dashboard?.id &&
                currentDashboard?.id &&
                !currentDashboard?.isSharedWithMe &&
                !layoutFirstTime
            ) {
                console.log("handleLayoutChange - updateDashboardWidgets");

                setLayoutFirstTime(false);
                await DashboardService.updateDashboardWidgets(currentDashboard);
            }
            setLayoutFirstTime(false);
        }

        Object.keys(refs.current).map((key) => {
            const widget: FixmeType = currentDashboard?.widgets.find((widget: FixmeType) => key === widget.id);
            if (widget?.pos?.sizeY > 1) {
                const { width, height } = refs.current?.[key].chart.container.parentNode.getBoundingClientRect();
                refs.current?.[key].chart?.setSize(width, height - 38, false);
            }
        });
    }

    const duplicateDashboard = async () => {
        DashboardService.cloneDashboard(currentDashboard)
            .then(async (createdDashboard) => {
                dispatch(setSharedWithMeDashboards());
                await keywordGroupsQueryClient.invalidateAll();
                proNavigator.go("dashboard.created", {
                    // @ts-ignore FIXME: strict migration - 'createdDashboard' is possibly 'undefined' — clone resolves to a dashboard on success
                    dashboardId: createdDashboard.id,
                });
            })
            .catch((err) => {
                console.warn("Error trying to duplicate dashboard: ", err);
            });
    };

    function sortWidgetsByPosition(widgets: FixmeType) {
        const sorted = sortBy(widgets, function (x: any) {
            return parseFloat(`${x.pos.row}.${x.pos.col}`);
        });
        return sorted;
    }

    function sortWidgetsForPptExport(widgets: FixmeType) {
        const sortedWidgetsByPosition = sortWidgetsByPosition(widgets);
        const supportedWidgets = sortedWidgetsByPosition.filter((widget) => widget.isPptSupported());
        const unsupportedWidgets = sortedWidgetsByPosition.filter((widget) => !widget.isPptSupported());
        return [...supportedWidgets, ...unsupportedWidgets];
    }

    /**
     * Opens a modal that allows the user to select which widgets he wants to export to powerpoint
     */
    const openExportPptModal = async () => {
        // We use the widgets index within the array as an id, since we know that
        // they will always maintain this order (they're sorted in a specific way)

        setWidgetsForPptModal(
            sortWidgetsForPptExport(currentDashboard?.widgets).map((widget: FixmeType, i: number) => {
                return {
                    id: `${i}`,
                    viewData: {
                        title: getWidgetTitle(widget),
                        subtitle: getWidgetSubtitleForModal(widget),
                    },
                    isSelected: true,
                    isDisabled: !widget.isPptSupported() || widget._metricConfig?.isDeprecated,
                };
            }),
        );
        setIsPptExportModalOpen(true);
        // return false;
    };

    const closeExportPptModal = () => {
        setIsPptExportModalOpen(false);
    };

    const onPptModalExportConfirm = async (widgetsToExport: IDashboardExportListItem[]) => {
        closeExportPptModal();

        const selectedWidgetIds = widgetsToExport
            .filter((widget) => widget.isSelected && !widget.isDisabled)
            .map((widget) => widget.id);

        const selectedWidgets = resolveSelectedPptWidgets(selectedWidgetIds);
        if (selectedWidgets.length <= 0) {
            return;
        }

        await exportWidgetsToPpt(selectedWidgets);
    };

    const exportWidgetsToPpt = async (widgets: FixmeType): Promise<void> => {
        SwTrack.all.trackEvent("Export", "submit-ok", "Dashboard/ppt/" + currentDashboard?.title);

        try {
            openPptExportLoader();

            const pptRequest: IPptExportRequest = {
                // @ts-ignore FIXME: strict migration - 'currentDashboard' is possibly 'null' — guaranteed set after the early null return above
                name: currentDashboard.title,
                metrics: widgets.map((widget: FixmeType) => widget.getDataForPpt()),
                meta: {
                    userEmail: swSettings?.user?.username,
                },
            };

            await pptService.current?.exportPpt(pptRequest);
        } finally {
            closeExportLoader();
        }
    };

    const resolveSelectedPptWidgets = (selectedIds: string[]) => {
        return sortWidgetsForPptExport(currentDashboard?.widgets).filter((_: FixmeType, id: number) => {
            return selectedIds.includes(`${id}`);
        });
    };

    const openPdfExportLoader = () => {
        setExportLoaderTitle(t("dashboard.export.loader.pdf.title"));
        setExportLoaderSubtitle(t("dashboard.export.loader.pdf.subtitle"));
        setIsExportLoading(true);
    };

    const openPptExportLoader = () => {
        setExportLoaderTitle(t("dashboard.export.loader.ppt.title"));
        setExportLoaderSubtitle(t("dashboard.export.loader.ppt.subtitle"));
        setIsExportLoading(true);
    };

    const closeExportLoader = () => {
        setIsExportLoading(false);
        setExportLoaderTitle("");
        setExportLoaderSubtitle("");
    };

    const downloadPdf = async () => {
        SwTrack.all.trackEvent("Download", "submit-ok", "Dashboard/pdf/" + currentDashboard?.title);
        openPdfExportLoader();
        const gridsterElement: HTMLDivElement = gridContainerRef.current as HTMLDivElement;
        if (!gridsterElement) {
            return;
        }
        PdfExportService.setHTML(gridsterElement.outerHTML, "dashboard-pdf");
        try {
            await PdfExportService.downloadHtmlPdfFedService(currentDashboard?.title);
        } finally {
            closeExportLoader();
        }
    };

    //This function is being used to open the wizard from React context (dashboardHeader).
    const openMetricsGallery = () => {
        const isQueryLimitReached = RecentService.isQueryLimiterEnabled();
        isQueryLimitReached ? RecentService.getQueryLimiterHook() : openWizard(newWidget);
    };

    function openWizard(widget: FixmeType) {
        setSelectedWidget(widget);
        setIsWizardOpen(true);

        if (widget.properties) {
            dispatch(dashboardWizardNewWidget());
        }

        if (widget.id) {
            dispatch(dashboardWizardSetWidgetId(widget.id));
        }
    }

    const openShareDashboard = () => {
        setShareDashboardOpen(true);
    };

    function getDashboardWebsites() {
        const websites: FixmeType[] = [];
        currentDashboard?.widgets.forEach((widget: FixmeType) => {
            const model = widget.getWidgetModel();
            if (model.family === "Website") {
                model.key.forEach((keyItem: FixmeType) => {
                    if (websites.indexOf(keyItem.name) === -1) {
                        websites.push(keyItem.name);
                    }
                });
            }
        });
        return websites;
    }

    type DomainUpdateAccumulator = {
        newKeys: IWidgetKeyBase[];
        newPageFilters: IChosenItemParams;
        keysForFetchingImages: string[];
    };

    const updateDashboardWidgetsDomain = (domainsMap: FixmeType) => {
        currentDashboard?.widgets.forEach(async (widget: FixmeType) => {
            const model = widget.getWidgetModel();
            if (model.family !== "Website") {
                return;
            }
            const hasPageFilter = !!(widget?.apiParams?.pageFilter as string);
            const uniqueKeys = new Set();

            const { newKeys, newPageFilters, keysForFetchingImages } = (
                (model?.key ?? []) as Array<IWidgetWebsiteKey>
            ).reduce<DomainUpdateAccumulator>(
                (acc: DomainUpdateAccumulator, key) => {
                    if (!uniqueKeys.has(key.name) && !uniqueKeys.has(domainsMap[key.name])) {
                        const keyWillBeUpdated = domainsMap[key.name] !== key.name;
                        if (keyWillBeUpdated) {
                            acc.keysForFetchingImages.push(domainsMap[key.name] as string);
                        }
                        const newKey: string = (domainsMap[key.name] as string) ?? key.name;
                        acc.newKeys.push({ name: newKey, type: "Website", image: key?.image });

                        if (hasPageFilter) {
                            const { subdomain, domain } = getUrlComponents(newKey);
                            // @ts-ignore FIXME: strict migration - Argument of type 'string | undefined' is not assignable to parameter of type 'string' — getUrlComponents may return undefined subdomain, util handles falsy
                            const domainWithSubdomain = getDomainWithSubdomain(domain, subdomain);
                            const newPageFilterItem = {
                                url: domainWithSubdomain,
                                searchType: (subdomain ? "subdomain" : "domain") as WebsitePageSearchType,
                            };
                            acc.newPageFilters.push(newPageFilterItem);
                        }
                        uniqueKeys.add(domainsMap[key.name]);
                    }
                    return acc;
                },
                { newKeys: [], newPageFilters: [], keysForFetchingImages: [] },
            );

            let keysWithImages = newKeys;
            if (keysForFetchingImages.length > 0) {
                const images = await fetchWebsitesFaviconsQuery(keysForFetchingImages);
                keysWithImages = newKeys.map((key) => (images?.[key.name] ? { ...key, image: images[key.name] } : key));
            }

            const updatedProperties = Object.assign({}, widget.getProperties(), { key: keysWithImages });
            if (hasPageFilter) {
                updatedProperties.filters = updatedProperties.filters || {};
                updatedProperties.filters.pageFilter = JSON.stringify(newPageFilters);
            }

            DashboardService.editWidget({
                properties: updatedProperties,
                pos: widget.pos,
                id: widget.id,
                dashboardId: widget.dashboardId,
            });
            widget.getLegendImage();
        });
    };

    function handleWizardClose(widget: FixmeType) {
        setSelectedWidget(widget || false);
        setIsWizardOpen(false);
    }

    const triggerSubscriptiondToast = () => {
        const text = t("dashboard.dashboardSubscription.success.toastUpdated");
        dispatch(showSuccessToast(text));
    };
    const onBreakpointChange = (breakpoint: FixmeType, numOfCols: number) => {
        setBreakpoint(breakpoint);

        setNumOfCols(numOfCols);
    };

    const onSubscription = () => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", "Subscribe");
        // @ts-ignore FIXME: strict migration - 'currentDashboard' is possibly 'null' — guaranteed set after the early null return above
        DashboardService.dashboardsubscribe(currentDashboard.id).then(async () => {
            setIsSubscriptionOn(await DashboardSubscriptionService.isSubscriptionOn(currentDashboard));
            setIsNotificationAllowed(DashboardSubscriptionService.isNotificationAllowed(currentDashboard));
            triggerSubscriptiondToast();
        });
    };

    const onUnsubscription = async () => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", "Unsubscribe");
        // @ts-ignore FIXME: strict migration - 'currentDashboard' is possibly 'null' — guaranteed set after the early null return above
        DashboardService.dashboardUnsubscribe(currentDashboard.id).then(() => {
            setIsSubscriptionOn(DashboardSubscriptionService.isSubscriptionOn(currentDashboard));
            setIsNotificationAllowed(DashboardSubscriptionService.isNotificationAllowed(currentDashboard));
        });
    };

    function findEmptyRow(layout: FixmeType) {
        const rows: boolean[] = [];
        layout.forEach((widget: FixmeType) => {
            rows[widget.y] = true;
            if (widget.h == 2) {
                rows[widget.y + 1] = true;
            }
        });
        for (let i = 0; i < rows.length; i++) {
            if (rows[i] !== true) {
                return i;
            }
        }
        return null;
    }

    function clearEmptyRows(oldLayout: FixmeType) {
        const newLayout = [...oldLayout];
        let emptyRow: number | null;
        const max = newLayout.length;
        let counter = 0;
        while ((emptyRow = findEmptyRow(newLayout)) !== null && counter < max) {
            const rowToShift = emptyRow;
            newLayout.forEach((widget: FixmeType) => {
                if (widget.y > rowToShift) {
                    widget.y = widget.y - 1;
                }
            });
            counter++;
        }
        return newLayout;
    }

    //handle all needed when WIZARD IS OPENED
    if (isWizardOpen) {
        return (
            <div
                className={"innerWrap dashboard-wrap dashboard-wrap-no-header"}
                style={{ width: "100%" }}
            >
                <div className="react-wizard-wrap">
                    <DashboardWizardV2
                        onClose={handleWizardClose}
                        selectedWidget={selectedWidget}
                    />
                </div>
            </div>
        );
    }

    function handleEdit(editedWidget: IWidget<any>) {
        dispatch(dashboardWizardIsEditing(true, editedWidget));
        openWizard(editedWidget);
    }

    function handleToggleEnlarge(widget: FixmeType) {
        // console.log("handleToggleEnlarge for", widget);
        handleEnlarge(widget);
    }

    const isLimitReached =
        segmentsLimitationStatus === SEGMENTS_LIMIT_STATUS_MAP.HARD_LIMIT ||
        segmentsLimitationStatus === SEGMENTS_LIMIT_STATUS_MAP.SOFT_LIMIT;

    return (
        <div
            className={classNames("innerWrap dashboard-wrap", {
                "dashboard-wrap-no-header": isWizardOpen,
            })}
            style={{ width: "100%" }}
        >
            {currentDashboard?.widgets?.length === 0 ? <EmprtyDashboard /> : null}
            <DashboardHeaderV2
                dashboard={currentDashboard}
                duplicateDashboard={duplicateDashboard}
                downloadPdf={downloadPdf}
                exportToPpt={openExportPptModal}
                shareDashboard={openShareDashboard}
                openMetricsGallery={openMetricsGallery}
                isSubscriptionOn={isSubscriptionOn}
                isNotificationAllowed={isNotificationAllowed}
                onSubscription={onSubscription}
                onUnsubscription={onUnsubscription}
                navigateToDashboard={navigateToDashboard}
                isSharingDashboard={isSharingDashboard}
            />

            {isLimitReached ? <SegmentsLimitBanner /> : null}

            <GridContainer
                ref={gridContainerRef}
                key={widgetsKey}
                marginTop={pageHeight}
            >
                <ReactGridLayout
                    autoSize={true}
                    className="layout"
                    breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 600, xxs: 400 }}
                    cols={{ lg: 4, md: 4, sm: 2, xs: 2, xxs: 1 }}
                    layouts={{ lg: layout }}
                    rowHeight={344}
                    margin={[25, 25]}
                    resizeHandle={<ResizeHandleCmp show={!isStatic} />}
                    draggableCancel={".grid-no-drag"}
                    compactType={"horizontal"}
                    useCSSTransforms={true}
                    onDragStop={handleDragged}
                    onResizeStop={handleResized}
                    onBreakpointChange={onBreakpointChange}
                    isDraggable={!isStatic}
                >
                    {widgets?.map((widget) => (
                        <CustomGridItemComponent
                            key={widget.id}
                            widget={widget}
                            onEdit={handleEdit}
                            onToggleEnlarge={handleToggleEnlarge}
                            setChart={(ref) => (refs.current[widget.id] = ref)}
                            isStatic={isStatic}
                            className={`${widget.pos.sizeX ? "widget-size-" + widget.pos.sizeX : ""} ${
                                widget.pos.sizeY ? "widget-height-" + widget.pos.sizeY : ""
                            } `}
                        />
                    ))}
                </ReactGridLayout>
            </GridContainer>

            {shareDashboardOpen && currentDashboard ? (
                <SharedDashboardModalContainer
                    setIsOpen={setShareDashboardOpen}
                    currentDashboard={currentDashboard}
                    setCurrentDashboard={setCurrentDashboard}
                    isSharingDashboard={isSharingDashboard}
                    setIsSharingDashboard={setIsSharingDashboard}
                />
            ) : null}

            {showGlobalFilters ? (
                <DashboardWebsiteSidebar
                    isOpen={isWebsitesSidebarOpen}
                    onSideBarClose={toggleWebsitesSidenav}
                    websites={getDashboardWebsites()}
                    onClickFunc={updateDashboardWidgetsDomain}
                />
            ) : null}
            <DashboardCopyToUserDialog />
            {showGlobalFilters ? (
                <div className="dashboardGlobalFilters">
                    <div className="dashboardGlobalFilters-section">
                        <h6>Country</h6>
                        <DashboardWizardCountry
                            availableCountries={userCountries}
                            changeCountry={globalChangeCountry}
                            selectedCountryId={0}
                        />
                    </div>
                    <div className="dashboardGlobalFilters-section">
                        <h6>Duration</h6>
                        <StatefulDropdown
                            items={userDurations}
                            selectedId={"-1"}
                            onSelect={globalChangeDuration}
                        />
                    </div>
                    <div className="dashboardGlobalFilters-section">
                        <h6>Device</h6>

                        <StatefulDropdown
                            items={userWebsources}
                            selectedId={"-1"}
                            onSelect={globalChangeWebsource}
                        />
                    </div>
                    <div className="dashboardGlobalFilters-section">
                        <h6>Websites</h6>
                        <button
                            className="dashboardGlobalFilters-button"
                            onClick={toggleWebsitesSidenav}
                        >
                            Search & Replace
                        </button>
                    </div>
                </div>
            ) : null}

            <DashboardPptExportModalContainer
                isOpen={isPptExportModalOpen}
                onClose={closeExportPptModal}
                onExport={onPptModalExportConfirm}
                widgets={widgetsForPptModal}
            />
            {isExportLoading ? (
                <DashboardLoader
                    title={exportLoaderTitle}
                    subtitle={exportLoaderSubtitle}
                />
            ) : null}

            <AddonPopup />
        </div>
    );
};
